import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EyeOff } from "lucide-react";

interface HiddenProductToastProps {
  message: string;
  undoLabel: string;
  onUndo: () => void;
  onDismiss: () => void;
  /** Stacks the toast above a sheet (the store picker sits at z-index 100000). */
  zIndex?: number;
}

export default function HiddenProductToast({ message, undoLabel, onUndo, onDismiss, zIndex }: HiddenProductToastProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;

  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(onDismiss, 6000);
    return () => window.clearTimeout(timer);
  }, [onDismiss, paused]);

  return createPortal(
    <div className="hidden-product-feedback pointer-events-none fixed inset-x-0 z-[9999] flex justify-center px-4" style={zIndex ? { zIndex } : undefined}>
      <div
        role="status"
        aria-live="polite"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="pointer-events-auto flex w-fit max-w-full items-center gap-3 rounded-2xl border border-primary-600/15 bg-white/95 py-2 pl-3 pr-2 font-sans shadow-[0_8px_30px_rgba(42,5,65,0.12)] backdrop-blur-md dark:border-purple-400/25 dark:bg-dark-900/95"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-primary-600 dark:bg-purple-500/15 dark:text-purple-300" aria-hidden="true">
          <EyeOff size={16} strokeWidth={1.8} />
        </span>
        <span className="min-w-0 text-[13px] font-semibold leading-5 text-gray-800 dark:text-gray-100">{message}</span>
        <button
          type="button"
          className="min-h-10 shrink-0 rounded-xl bg-purple-50 px-3 text-xs font-bold text-primary-600 transition-colors hover:bg-purple-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-purple-500/15 dark:text-purple-300 dark:hover:bg-purple-500/25"
          onClick={() => { onDismiss(); onUndo(); }}
        >
          {undoLabel}
        </button>
      </div>
    </div>,
    document.body
  );
}
